import {
  Text as ChakraText,
  defineRecipe,
  useRecipe,
  TextProps,
} from '@chakra-ui/react';
import { forwardRef } from 'react';

const textRecipe = defineRecipe({
  base: {
    color: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
  },
  variants: {
    visual: {
      black: { color: 'fgPrimary' },
      blackTotal: { color: 'black' },
      gray: { color: 'fgSecondary' },
      grayTotal: { color: 'gray' },
      lightGray: { color: 'bgCompSecondaryTiny' },
      green: { color: 'fgSuccess' },
      orange: { color: 'fgWarning' },
      red: { color: 'fgDanger' },
      blue: { color: 'fgInteractive' },
      white: { color: 'fgPrimaryInverse' },
      whiteTotal: { color: 'white' },
    },
    size: {
      xsm: { base: { fontSize: '0.625rem', lineHeight: '0.75rem' } },
      small: { base: { fontSize: 'xs', lineHeight: '1rem' } },
      meta: { base: { fontSize: 'sm', lineHeight: '1.25rem' } },
      body: { base: { fontSize: '1rem', lineHeight: '1.5rem' } },
      smallHeading: { base: { fontSize: '1rem', lineHeight: '1.25rem' } },
      heading: { base: { fontSize: '1.25rem', lineHeight: '1.5rem' } },
      semiLargeHeading: {
        base: { fontSize: '1.5rem', lineHeight: '1.75rem' },
      },
      largeHeading: { base: { fontSize: '2rem', lineHeight: '2.25rem' } },
      extraLargeHeading: { base: { fontSize: '2.5rem' } },
    },
  },
  defaultVariants: {
    visual: 'black',
    size: 'meta',
  },
});

export type TYPOGRAPHY_COLORS =
  | 'black'
  | 'blackTotal'
  | 'gray'
  | 'grayTotal'
  | 'lightGray'
  | 'green'
  | 'orange'
  | 'red'
  | 'blue'
  | 'white'
  | 'whiteTotal';

type TypographyProps = Omit<TextProps, 'color'> & {
  type?:
    | 'xsm'
    | 'small'
    | 'meta'
    | 'body'
    | 'smallHeading'
    | 'heading'
    | 'semiLargeHeading'
    | 'largeHeading'
    | 'extraLargeHeading';
  color?: TYPOGRAPHY_COLORS;
  cropped?: boolean;
  dataTestid?: string;
};

export const Typography = forwardRef<HTMLParagraphElement, TypographyProps>(
  ({ type, color, cropped, dataTestid, ...props }, ref) => {
    const recipe = useRecipe({ recipe: textRecipe });
    const styles = recipe({ visual: color, size: type });

    return (
      <ChakraText
        ref={ref}
        css={styles}
        data-testid={dataTestid}
        truncate={cropped}
        {...props}
      />
    );
  }
);

Typography.displayName = 'Typography';
